import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import SafeLink from "../../../components/SafeLink";
import useFocusTrap from "../../../hooks/useFocusTrap";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Btn from "../../../components/v3/Btn";
import Pill from "../../../components/v3/Pill";
import { formatAuditTimestamp, daysUntilUtc } from "../../../utils/fmtDate";
import { bdcApi } from "../../../services/bdcApi";
import { drApi } from "../../../services/drApi";
import { useRole } from "../../../hooks/useRole";
import { apiErrorCode, apiErrorMessage } from "../../../utils/apiError";

const MIN_NOTES = 10;
const STATUS_CLASS = {
  APPROVED: "s-active",
  PENDING:  "s-rev",
  REJECTED: "s-fail",
  EXPIRED:  "s-fail",
};
const SELF_REVIEW = "You cannot approve your own credential.";
// Confirming a registration needs an APPROVED credential, and approving one
// used to drop it straight out of this list: the row vanished before the
// second half of onboarding a reviewer could be done at all.
const LIST_STATUS = "PENDING,APPROVED";

const backdropStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  zIndex: 1000, display: "grid", placeItems: "center", padding: 20,
};
const dialogStyle = {
  background: "var(--bg-card)", border: "1px solid var(--bd)",
  borderRadius: "var(--r-md)", padding: 20, maxWidth: 560, width: "100%",
  maxHeight: "90vh", overflow: "auto",
  boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
};

function CredentialApprovals() {
  // Approving a credential is no longer a Platform Manager monopoly: a CCO or
  // UDP reviews them too, and only those two can confirm the regulatory
  // registration behind an approved one.
  const { canReview } = useRole();
  const [confirmingId, setConfirmingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(null);
  const [decision, setDecision] = useState("APPROVED");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState(null);
  const dialogRef = useFocusTrap({ active: !!open, onClose: () => !submitting && setOpen(null) });

  function fetchList() {
    setLoading(true);
    bdcApi
      .adListPendingCredentials({ page, limit, status: LIST_STATUS })
      .then((res) => {
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  function openDialog(row) {
    setOpen(row);
    setDecision("APPROVED");
    setNotes("");
  }

  function closeDialog() {
    if (submitting) return;
    setOpen(null);
    setNotes("");
  }

  async function submit(ev) {
    ev.preventDefault();
    if (notes.trim().length < MIN_NOTES) {
      setFlash({ type: "danger", message: `Notes must be at least ${MIN_NOTES} characters.` });
      return;
    }
    setSubmitting(true);
    try {
      const res = await bdcApi.adDecideCredential(open.id, {
        decision,
        review_notes: notes.trim(),
      });
      // The API accepts a self-review in the platform-wide bootstrap case (the
      // first CCO has nobody senior to approve them), so report the status it
      // actually recorded rather than the one we asked for.
      const recorded = res?.data?.status || decision;
      setFlash({ type: "info", message: `Credential #${open.id} ${String(recorded).toLowerCase()}.` });
      setOpen(null);
      setNotes("");
      fetchList();
    } catch (err) {
      const selfReview =
        err?.response?.status === 409 && apiErrorCode(err) === "self_review";
      setFlash({
        type: "danger",
        message: selfReview ? SELF_REVIEW : apiErrorMessage(err, "Decide failed"),
      });
    } finally {
      setSubmitting(false);
    }
  }

  // Registration confirmation is the second half of onboarding a reviewer: the
  // credential is approved, and then a CCO or UDP confirms the person is
  // registered with the regulator. Only the second half is gated on canReview.
  async function confirmRegistration(row) {
    setConfirmingId(row.id);
    setFlash(null);
    try {
      await drApi.confirmRegistration(row.id, {});
      setFlash({ type: "info", message: `Registration confirmed for credential #${row.id}.` });
      fetchList();
    } catch (err) {
      const selfReview =
        err?.response?.status === 409 && apiErrorCode(err) === "self_review";
      setFlash({
        type: "danger",
        message: selfReview ? SELF_REVIEW : apiErrorMessage(err, "Confirm failed"),
      });
    } finally {
      setConfirmingId(null);
    }
  }

  function expiryBadge(dateStr) {
    if (!dateStr) return <span className="muted" style={{ fontSize: "var(--t-xs)" }}>no date</span>;
    const daysLeft = daysUntilUtc(dateStr);
    if (daysLeft === null) {
      return <span className="muted" style={{ fontSize: "var(--t-xs)" }}>no date</span>;
    }
    let cls = "s-active";
    if (daysLeft < 0) cls = "s-fail";
    else if (daysLeft < 30) cls = "s-rev";
    const dateOnly = String(dateStr).slice(0, 10);
    return (
      <span className={`tbl-status ${cls}`}>
        {dateOnly} ({daysLeft >= 0 ? `${daysLeft}d left` : `${Math.abs(daysLeft)}d past`})
      </span>
    );
  }

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title="DR credential approvals"
        subtitle={<><span className="mono">{total}</span><span>credentials · page {page} of {Math.max(totalPages, 1)}</span></>}
      />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        Reviewer credentials awaiting a decision. Approving grants access to the
        approval workflows; rejecting keeps it locked out. Notes are
        audit-logged. Confirming the registration behind an approved credential
        is a CCO or UDP action.
      </p>

      {flash && <Alert tone={flash.type === "ok" ? "info" : flash.type}>{flash.message}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      {rows.length === 0 ? (
        <Empty>No credentials awaiting approval.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Holder</th>
                  <th>Type</th>
                  <th>License #</th>
                  <th>Issuer</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Document</th>
                  <th>Registration</th>
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td className="mono">#{c.id}</td>
                    <td>
                      <div>{c.holder?.name || `Reviewer #${c.dr_user_id}`}</div>
                      <div className="tbl-id">{c.holder?.email || `#${c.dr_user_id}`}</div>
                    </td>
                    <td><Pill tone="blue">{c.credential_type}</Pill></td>
                    <td className="mono">{c.license_number || "—"}</td>
                    <td className="muted">{c.issuer || "—"}</td>
                    <td>{expiryBadge(c.expiry_date)}</td>
                    <td>
                      <span className={`tbl-status ${STATUS_CLASS[c.status] || "s-mute"}`}>
                        {c.status || "PENDING"}
                      </span>
                    </td>
                    <td>
                      {c.document_url
                        ? <SafeLink href={c.document_url}>View</SafeLink>
                        : <span className="muted">—</span>}
                    </td>
                    <td className="muted">
                      {c.registration_confirmed_at
                        ? formatAuditTimestamp(c.registration_confirmed_at)
                        : "Not confirmed"}
                    </td>
                    <td className="muted">{formatAuditTimestamp(c.createdAt)}</td>
                    <td className="r">
                      {canReview && c.status === "APPROVED" && !c.registration_confirmed_at && (
                        <Btn
                          size="sm"
                          disabled={confirmingId === c.id}
                          onClick={() => confirmRegistration(c)}
                        >
                          {confirmingId === c.id ? "Confirming..." : "Confirm registration"}
                        </Btn>
                      )}
                      {(!c.status || c.status === "PENDING") && (
                        <Btn variant="primary" size="sm" onClick={() => openDialog(c)}>Review</Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page === 1 || submitting} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>{page} / {totalPages}</span>
          <Btn size="sm" disabled={page === totalPages || submitting} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}

      {open && (
        <div
          style={backdropStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-cred-dialog-title"
          ref={dialogRef}
        >
          <form onSubmit={submit} style={dialogStyle}>
            <h3 id="ad-cred-dialog-title" style={{ fontSize: "var(--t-xl)", fontWeight: 600, margin: "0 0 12px" }}>
              Review credential #{open.id}
            </h3>
            <dl style={{ margin: "0 0 16px" }}>
              <KV
                label="Holder"
                value={
                  open.holder?.name
                    ? `${open.holder.name} (${open.holder.email || `#${open.dr_user_id}`})`
                    : `#${open.dr_user_id}`
                }
              />
              <KV label="Type" value={open.credential_type} />
              <KV label="License #" value={open.license_number || "—"} />
              <KV label="Issuer" value={open.issuer || "—"} />
              <KV label="Expires" value={expiryBadge(open.expiry_date)} />
              <KV label="Document" value={open.document_url ? <SafeLink href={open.document_url}>{open.document_url}</SafeLink> : "—"} />
            </dl>

            <fieldset style={{ border: 0, padding: 0, margin: "0 0 12px" }}>
              <legend className="field__label" style={{ marginBottom: 6 }}>Decision</legend>
              <div style={{ display: "flex", gap: 12 }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "var(--t-md)" }}>
                  <input type="radio" name="decision" value="APPROVED"
                    checked={decision === "APPROVED"}
                    onChange={() => setDecision("APPROVED")}
                    disabled={submitting}
                  />
                  Approve
                </label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "var(--t-md)" }}>
                  <input type="radio" name="decision" value="REJECTED"
                    checked={decision === "REJECTED"}
                    onChange={() => setDecision("REJECTED")}
                    disabled={submitting}
                  />
                  Reject
                </label>
              </div>
            </fieldset>

            <div className="field">
              <label className="field__label" htmlFor="ad-cred-notes">
                Review notes (required, min {MIN_NOTES} chars)
              </label>
              <textarea
                id="ad-cred-notes"
                className="field__textarea"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={submitting}
                autoFocus
                required
                minLength={MIN_NOTES}
              />
              <span className="field__hint">{notes.trim().length} / {MIN_NOTES}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <Btn type="button" onClick={closeDialog} disabled={submitting}>Cancel</Btn>
              <Btn variant="primary" type="submit" disabled={submitting || notes.trim().length < MIN_NOTES}>
                {submitting ? "Submitting…" : `Confirm ${decision.toLowerCase()}`}
              </Btn>
            </div>
          </form>
        </div>
      )}
    </PageShell>
  );
}

const KV = ({ label, value }) => (
  <>
    <dt className="field__label" style={{ marginTop: 8 }}>{label}</dt>
    <dd style={{ margin: "2px 0 0", fontSize: "var(--t-md)" }}>{value}</dd>
  </>
);

export default CredentialApprovals;
